import React, { useState, useEffect } from "react";
import AdminNavBar from "./AdminNavBar"; // Make sure this component also has the desired styling
import { collection, onSnapshot, deleteDoc, doc, query, where } from "firebase/firestore";
import { firestore } from "../../../firebase";
import AddShopPopup from "./AddShopPopup";

// --- MUI Imports ---
import {
  Fab,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box, // Added
  Container, // Added
  Typography, // Added
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
// --- END MUI Imports ---

function AddShop() {
  const [shops, setShops] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [shopToDelete, setShopToDelete] = useState(null);
  const tenantId = sessionStorage.getItem("tenantId") || "";
  const userRole = sessionStorage.getItem("userRole") || "";

  useEffect(() => {
    if (userRole !== "superadmin" && !tenantId) {
      setShops([]);
      return () => {};
    }

    const shopsRef = collection(firestore, "shops");
    const shopsQuery =
      userRole === "superadmin"
        ? shopsRef
        : query(shopsRef, where("tenantId", "==", tenantId));

    const unsubscribe = onSnapshot(
      shopsQuery,
      (snapshot) => {
        const shopsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setShops(shopsData);
      },
      (error) => {
        console.error("Error fetching shops data: ", error);
      }
    );
    return () => unsubscribe();
  }, [tenantId, userRole]);

  const handleOpenPopup = () => setOpenPopup(true);
  const handleClosePopup = () => setOpenPopup(false);

  const handleOpenDeleteDialog = (shopId) => {
    setShopToDelete(shopId);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setShopToDelete(null);
  };

  const handleDeleteShop = async () => {
    if (shopToDelete) {
      try {
        await deleteDoc(doc(firestore, "shops", shopToDelete));
        handleCloseDeleteDialog();
      } catch (error) {
        console.error("Error deleting shop: ", error);
      }
    }
  };

  return (
    // Use Box for overall flex layout instead of div
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Assuming AdminNavBar is meant to be the top bar */}
      <AdminNavBar />

      {/* Main content area */}
      <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "grey.100" }}>
        <Container maxWidth="lg">
          {" "}
          {/* Adjust maxWidth as needed */}
          <Paper sx={{ p: { xs: 2, md: 3 }, boxShadow: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Manage Business Vendor
            </Typography>

            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      Establishment Name
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      Establishment Number
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      Establishment Email{" "}
                      {/* Changed from Address based on screenshot data */}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shops.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ p: 5 }}>
                        No shops added yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    shops.map((item) => (
                      <TableRow hover key={item.id}>
                        <TableCell align="center" sx={{ fontWeight: "medium" }}>
                          {" "}
                          {/* Made name bold */}
                          {item.shopName}
                        </TableCell>
                        <TableCell align="center">
                          {item.shopNumber || "N/A"}
                        </TableCell>
                        <TableCell align="center">
                          {item.email || "N/A"}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Delete Shop">
                            <IconButton
                              onClick={() => handleOpenDeleteDialog(item.id)}
                              aria-label="delete"
                              color="error" // Use color prop
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Container>
      </Box>

      {/* Add Business Vendor FAB */}
      <Tooltip title="Add New Business Vendor">
        <Fab
          aria-label="add"
          onClick={handleOpenPopup}
          sx={{
            position: "fixed",
            bottom: 30,
            right: 30,
            // Apply theme gradient
            background: "linear-gradient(to right, #6A5ACD, #FF69B4)",
            color: "white",
            "&:hover": {
              background: "linear-gradient(to right, #5a4bcd, #ef59a4)",
            },
          }}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      {/* Popups/Dialogs */}
      <AddShopPopup open={openPopup} handleClose={handleClosePopup} />

      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Shop</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this shop? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteShop} color="error">
            {" "}
            {/* Changed color */}
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AddShop;
